/** Deadline covers headers and the complete body of this JSON API transport. */
export async function fetchWithDeadline(
  url: string,
  options: RequestInit = {},
  timeoutMs = 30_000,
): Promise<Response> {
  const controller = new AbortController()
  const abort = () => controller.abort(options.signal?.reason)
  if (options.signal?.aborted) abort()
  else options.signal?.addEventListener('abort', abort, { once: true })
  let rejectAbort!: () => void
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAbort = () =>
      reject(
        controller.signal.reason ??
          new DOMException('Request aborted', 'AbortError'),
      )
    controller.signal.addEventListener('abort', rejectAbort, { once: true })
    if (controller.signal.aborted) rejectAbort()
  })
  const timer = setTimeout(
    () =>
      controller.abort(new DOMException('Request timed out', 'TimeoutError')),
    timeoutMs,
  )
  try {
    const response = await Promise.race([
      fetch(url, { ...options, signal: controller.signal }),
      aborted,
    ])
    // Some fetch wrappers expose an empty stream for bodyless responses.
    // Response rejects even an empty ArrayBuffer for these statuses.
    const isBodyless =
      options.method?.toUpperCase() === 'HEAD' ||
      response.status === 204 ||
      response.status === 205 ||
      response.status === 304
    const body =
      !isBodyless && response.body
        ? await Promise.race([response.arrayBuffer(), aborted])
        : null
    return new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    })
  } finally {
    clearTimeout(timer)
    controller.signal.removeEventListener('abort', rejectAbort)
    options.signal?.removeEventListener('abort', abort)
  }
}
